import { loadData, type ModelPricing, type AgentRecommendation } from "@fathom/core";
import { RemoteManifest, type FetchResult, type FetcherOptions } from "./types.js";

const MANIFEST_URL =
  "https://raw.githubusercontent.com/anthropics/fathom/main/data/remote-manifest.json";

const DEFAULT_TIMEOUT_MS = 10_000;

interface PricingData {
  models: ModelPricing[];
  recommendations: Record<string, AgentRecommendation>;
  manifestVersion: number;
  manifestDate: string;
}

export async function fetchPricing(
  options: FetcherOptions = {},
): Promise<FetchResult<PricingData>> {
  const now = new Date().toISOString();

  if (options.offline) {
    return loadLocalPricing(now);
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    );

    if (options.verbose) {
      console.log(`  Fetching: ${MANIFEST_URL}`);
    }

    const response = await fetch(MANIFEST_URL, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const json = await response.json();
    const manifest = RemoteManifest.parse(json);

    return {
      data: {
        models: manifest.models,
        recommendations: manifest.recommendations,
        manifestVersion: manifest.version,
        manifestDate: manifest.updated,
      },
      source: "remote",
      stale: false,
      fetchedAt: now,
    };
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : String(err);
    if (options.verbose) {
      console.log(`  Remote fetch failed: ${errorMsg}`);
    }
    return loadLocalPricing(now, errorMsg);
  }
}

function loadLocalPricing(
  fetchedAt: string,
  error?: string,
): FetchResult<PricingData> {
  const data = loadData();
  return {
    data: {
      models: data.models,
      recommendations: data.recommendations as Record<string, AgentRecommendation>,
      manifestVersion: 0,
      manifestDate: "local",
    },
    source: "local",
    stale: true,
    fetchedAt,
    error: error ?? "offline mode",
  };
}
